import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'bootstrap';
import Select from 'react-select'

import type { Contact } from '../../types';

import "intl-tel-input/build/css/intlTelInput.css";

import countries from '../../components/countries.json';

import infoIcon from '../../assets/icons/info.svg?raw&inline';

declare global {
  interface Window {
    intlTelInput: any;
  }
}

/** 
 * TODO refactor this by separating form and modal 
 * - the index is also used to determine if the member is new or not
 * and set the cargo to empty string, which in pandata context kybcontact.cargo if it empty
 * will mean is a shareholder custom added, not a cargo from rp 
*/
export const CompanyRoleFormModal: React.FC<CompanyRoleFormModalProps> = ({
  modalId, 
  index, 
  member, 
  globalStake, 
  onMemberUpdate, 
}) => {
  const { t } = useTranslation();

  const countriesList = countries.map((country) => country.name_es);

  const [nombre, setNombre] = useState(member.nombre);
  const [cargo, setCargo] = useState(member.cargo);
  const [email, setEmail] = useState(member.email);
  const [directorNominal, setDirectorNominal] = useState<boolean | null>(member.director_nominal);
  const [pep, setPep] = useState<boolean | null>(member.pep);
  const [pepCargo, setPepCargo] = useState(member.pep_cargo || '');
  const [pepEntidad, setPepEntidad] = useState(member.pep_entidad || '');
  const [pepFechaInicio, setPepFechaInicio] = useState(member.pep_fecha_inicio || '');
  type NationalityOption = { value: string; label: string };
  const [nacionalidad, setNacionalidad] = useState<string[]>(
    (member.nacionalidad ? member.nacionalidad.split(',').map(s => s.trim()).filter(Boolean) : [])
  );
  const [beneficiario, setBeneficiario] = useState<boolean | null>(member.beneficiario);
  const [porcentajeBeneficio, setPorcentajeBeneficio] = useState(Number(member.porcentaje_beneficio));
  const [error, setError] = useState<string | null>(null);
  const [intlInput, setIntlInput] = useState<any>(null);

  const [currentStep, setCurrentStep] = useState(1);

  const [touched, setTouched] = useState({
    nombre: false,
    cargo: false,
    telefono: false,
    email: false,
    beneficiario: false,
    porcentajeBeneficio: false,
    porcentaje_beneficio: false,
    nacionalidad: false,
    director_nominal: false,
    pep: false,
    pep_cargo: false,
    pep_entidad: false,
    pep_fecha_inicio: false,
  });

  const [errors, setErrors] = useState({
    nombre: false,
    cargo: false,
    telefono: false,
    email: false,
    beneficiario: false,
    porcentajeBeneficio: false,
    porcentaje_beneficio: false,
    nacionalidad: false,
    director_nominal: false,
    pep: false,
    pep_cargo: false,
    pep_entidad: false,
    pep_fecha_inicio: false,
  });

  const isNewBeneficiary = () => index < 0;

  const renderNameInitials = (name: string) => {
    if (name === '' || name === null || name === undefined) return '';

    const initials = name.split(' ').map((word) => word[0]).join('');
    return initials
  };

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "nombre":
        return value.trim() !== '' && /^[a-zA-Z\s]+$/.test(value);
      case "telefono":
        return intlInput.isValidNumber();
      case "email":
        const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return regex.test(value);
      case "porcentaje_beneficio":
      case "porcentajeBeneficio":
        const currentStake = member.porcentaje_beneficio ? Number(member.porcentaje_beneficio) : 0;
        const adjustedGlobalStake = globalStake - currentStake;
        return (Number(value) + adjustedGlobalStake) <= 100;
      default:
        return true;
    }
  };

  const hasErrors = () => {
    return Object.values(errors).some((error) => error);
  };

  const resetErrorsAndTouched = () => {
    setCurrentStep(1);
    setError(null);
    setErrors({
      nombre: false,
      cargo: false,
      telefono: false,
      email: false,
      beneficiario: false,
      porcentajeBeneficio: false,
      porcentaje_beneficio: false,
      nacionalidad: false,
      director_nominal: false,
      pep: false,
      pep_cargo: false,
      pep_entidad: false,
      pep_fecha_inicio: false
    });
    setTouched({
      nombre: false,
      cargo: false,
      telefono: false,
      email: false,
      beneficiario: false,
      porcentajeBeneficio: false,
      porcentaje_beneficio: false,
      nacionalidad: false,
      director_nominal: false,
      pep: false,
      pep_cargo: false,
      pep_entidad: false,
      pep_fecha_inicio: false,
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    switch (name) {
      case 'nombre':
        setNombre(value);
        break;
      case 'cargo':
        setCargo(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'nacionalidad':
        setNacionalidad(value ? value.split(',').map(v => v.trim()).filter(Boolean) : []);
        break;
      case 'pep_cargo':
        setPepCargo(value);
        break;
      case 'pep_entidad':
        setPepEntidad(value);
        break;
      case 'pep_fecha_inicio':
        setPepFechaInicio(value);
        break;
      case 'porcentaje_beneficio':
      case 'porcentajeBeneficio':
        setPorcentajeBeneficio(Number(value));
        setErrors((prev) => ({ ...prev, [name]: !validateField(name, value) }));
        break;
      default:
        break;
    }
  };

  // removed legacy native-select handler

  const handleBeneficiaryChange = (value: boolean) => {
    setBeneficiario(value);

    if (!value) setPorcentajeBeneficio(0);
  };

  const handleDirectorNominalChange = (value: boolean) => {
    setDirectorNominal(value);
  };

  const handlePepChange = (value: boolean) => {
    setPep(value);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;

    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: !validateField(name, value) }));
  };

  const validateFirstStep = () => {
    let valid = true;
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isEmailValid = regex.test(email);
    if (nombre === '' || intlInput.getNumber(window.intlTelInput.utils.numberFormat.E164) === '' || !isEmailValid || nacionalidad.length === 0) {

      setError(t('kyb_flow.org_members_card.modal.error'));

      setErrors(prev => ({
        ...prev,
        nombre: nombre === '',
        telefono: intlInput.getNumber(window.intlTelInput.utils.numberFormat.E164) === '',
        email: !isEmailValid,
        nacionalidad: nacionalidad.length === 0,
      }));

      setTouched(prev => ({
        ...prev,
        nombre: true,
        telefono: true,
        email: true,
        nacionalidad: true,
      }));

      valid = false;
    }

    return valid;
  };

  const onSubmit = () => {
    setError(null);

    if (currentStep === 1) {
      if (!validateFirstStep()) return;

      setCurrentStep(2);
      return;
    }

    const memberData: Contact = {
      nombre: nombre,
      cargo: cargo,
      telefono: intlInput.getNumber(window.intlTelInput.utils.numberFormat.E164),
      email: email,
      nacionalidad: nacionalidad.join(', '),
      beneficiario: beneficiario,
      porcentaje_beneficio: porcentajeBeneficio.toString(),
      director_nominal: directorNominal,
      pep: pep,
      pep_cargo: pepCargo,
      pep_entidad: pepEntidad,
      pep_fecha_inicio: pepFechaInicio,
      claim_request_id: member.claim_request_id,
    };

    if (memberData.director_nominal === null || memberData.pep === null ||
      (memberData.beneficiario && Number(memberData.porcentaje_beneficio) === 0) || 
      (memberData.pep && memberData.pep_entidad === '' && memberData.pep_cargo === '' && memberData.pep_fecha_inicio === '')) {
      setError(t('kyb_flow.org_members_card.modal.error'));
      return;
    }

    if (member.id) memberData.id = member.id;

    onMemberUpdate(index, memberData);
    const modalElement = document.getElementById('roleModal');
    const modal = Modal.getOrCreateInstance(modalElement as Element);

    modal.hide();
    setCurrentStep(1);
  };

  useEffect(() => {
    const input = document.querySelector("#inputNumer");

    const intlInputInstance = window.intlTelInput(input, {
      utilsScript: "https://cdn.jsdelivr.net/npm/intl-tel-input@24.3.4/build/js/utils.js",
      containerClass: 'intl-tel-input',
      initialCountry: 'pa',
      separateDialCode: true,
    });

    setIntlInput(intlInputInstance);
  }, []);
  useEffect(() => {
    setNombre(member.nombre);
    setCargo(index < 0 ? '' : member.cargo);
    setBeneficiario(index < 0 ? true : member.beneficiario);
    setEmail(member.email);
    setPorcentajeBeneficio(Number(member.porcentaje_beneficio));
    setDirectorNominal(member.director_nominal);
    setPep(member.pep);
    setPepCargo(member.pep_cargo || '');
    setPepEntidad(member.pep_entidad || '');
    setPepFechaInicio(member.pep_fecha_inicio || '');
    setNacionalidad(member.nacionalidad ? member.nacionalidad.split(',').map(s => s.trim()).filter(Boolean) : []);

    if (intlInput) {
      intlInput.setNumber(member.telefono || '');
    }

    resetErrorsAndTouched();

  }, [index, member, intlInput, t]);

  return (
    <div 
      className="modal fade" 
      id={modalId} 
      tabIndex={-1} 
      aria-labelledby={`${modalId}-label`} 
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="col-12 modal-title text-center size-20 font-weight-600" id={`${modalId}-label`}>
              {!isNewBeneficiary() ? member.cargo : t('kyb_flow.beneficiaries.beneficiaries')}
            </h5>
          </div>
          <div className="modal-body">
            { !isNewBeneficiary() &&
              <div className="row mb-2">
                <div className="col-2">
                  <span className="dot-1 d-flex align-items-center justify-content-center">
                    <b className={`text-white w-100 text-center font-weight-bold ${renderNameInitials(member.nombre || '').length > 2 ? 'size-18' : 'size-24'}`}>
                      {renderNameInitials(member.nombre || '')}
                    </b>
                  </span>
                </div>
                <div className="col-10 pt-2 ps-0">
                  <h4 className="size-14 font-weight-600 text-dark mb-0">{member.nombre}</h4>
                  <i className="size-14 font-weight-400 text-muted font-italic">{member.cargo}</i>
                </div>
              </div>
            }
            { !isNewBeneficiary() && <div className='row pt-2'><hr></hr></div> }
            <div className="row px-1">
              <form>
                {error && <label className="text-danger mb-2">{error}</label>}
                {/* Step 1 */}
                <div className={`form-step ${currentStep === 1 ? '' : 'd-none'}`}>
                  <label className="text-dark size-16 font-weight-600 mb-3">
                    {t('kyb_flow.org_members_card.modal.general_information')}
                  </label>
                  { isNewBeneficiary() &&
                  <div className="mb-3">
                    <label htmlFor="inputName" className="form-label">{t('kyb_flow.org_members_card.modal.name')}</label>
                    <input className="form-control less-rounded" id="inputName" aria-describedby="nameHelp" name='nombre' 
                      value={nombre} onChange={handleInputChange} onBlur={handleBlur}/>
                    {touched.nombre && errors.nombre && 
                      <p className="text-danger mb-0">{t('kyb_flow.beneficiaries.invalid_name')}</p>}
                  </div>}
                  <div className="mb-3">
                    <label htmlFor="inputEmail" className="form-label">{t('kyb_flow.org_members_card.modal.email')}</label>
                    <input type="email" name='email' className="form-control less-rounded" id="inputEmail" aria-describedby="emailHelp" 
                      value={email} onChange={handleInputChange} onBlur={handleBlur}/>
                    {touched.email && errors.email && 
                      <p className="text-danger mb-0">{t('kyb_flow.beneficiaries.invalid_email')}</p>}
                  </div>
                  <div className="mb-3 d-flex flex-column">
                    <label htmlFor="inputNumer" className="form-label">{t('kyb_flow.org_members_card.modal.phone_number')}</label>
                    <input type="tel" className="form-control less-rounded" id={`inputNumer`} name='telefono' onBlur={handleBlur}/>
                    {(touched.telefono && errors.telefono) && 
                      <p className="text-danger mb-0">{t('kyb_flow.beneficiaries.invalid_phone')}</p>}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="inputNacionalidad" className="form-label mb-0">{t('kyb_flow.org_members_card.modal.nationality')}</label>
                    <p className="text-muted size-14 font-weight-400 mb-1">{t('kyb_flow.org_members_card.modal.select_nationality')}</p>
                    <Select<NationalityOption, true>
                      options={countriesList.map((country) => ({ value: country, label: country }))}
                      name='nacionalidad'
                      className=""
                      id="inputNacionalidad"
                      placeholder=""
                      aria-describedby="nacionalidadHelp"
                      isMulti
                      value={nacionalidad.map((n) => ({ value: n, label: n }))}
                      onChange={(options) => setNacionalidad((options || []).map((o) => o.value))}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>
                {/* Step 2 */}
                <div className={`form-step ${currentStep === 2 ? '' : 'd-none'}`}>
                  <label className="text-dark size-16 font-weight-600 mb-3">
                    {t('kyb_flow.org_members_card.modal.legal_information')}
                  </label>
                  { !isNewBeneficiary() &&
                  <div className="mb-3">
                    <label className="form-label">{t('kyb_flow.org_members_card.modal.stakeholer')}</label>
                    <div className="d-flex">
                      <button
                        type="button"
                        className={`btn ${beneficiario === true ? 'btn btn-white-selected me-2 px-4' : 'btn btn-white me-2 px-4'}`}
                        onClick={() => handleBeneficiaryChange(true)}
                      >
                        {t('kyb_flow.org_members_card.modal.yes')}
                      </button>
                      <button
                        type="button"
                        className={`btn ${beneficiario === false ? 'btn btn-white-selected px-4' : 'btn btn-white px-4'}`}
                        onClick={() => handleBeneficiaryChange(false)}
                      >
                        {t('kyb_flow.org_members_card.modal.no')}
                      </button>
                    </div>
                  </div>}
  
                  {(isNewBeneficiary() || beneficiario) &&
                    <div className="mb-3">
                      <label htmlFor="inputNumerShareHolder" className="form-label">{t('kyb_flow.org_members_card.modal.participacion_accionaria')}</label>
                      <div className="d-flex align-items-center">
                        <input name='porcentaje_beneficio' pattern="[0-9]" className="form-control less-rounded w-auto" id="inputNumerShareHolder" style={{ maxWidth: 100 }}
                          value={porcentajeBeneficio} onChange={handleInputChange} onBlur={handleBlur} />
                        <span className="text-muted ms-2">%</span>
                      </div>
                      {touched.porcentaje_beneficio && errors.porcentaje_beneficio && 
                        <p className="text-danger mb-0">{t('kyb_flow.beneficiaries.shares_error')}</p>}
                    </div>
                  }
                  <div className="mb-3">
                    <label htmlFor="inputDirectorNominal" className="form-label d-flex">
                      {t('kyb_flow.org_members_card.modal.director_nominal')}
                      <i 
                        className="size-14 font-weight-400 text-muted font-italic" 
                        data-bs-toggle="tooltip" 
                        data-bs-placement="bottom" 
                        title={t('kyb_flow.org_members_card.modal.director_nominal_tooltip')}
                      >
                        <span 
                          style={{ width: 15 }}
                          className="d-flex align-items-center text-muted size-14 cursor-pointer ms-1" 
                          dangerouslySetInnerHTML={{ __html: infoIcon }} 
                        />
                      </i>
                    </label>
                    <div className="d-flex">
                      <button type="button" className={`btn ${directorNominal === true ? 'btn btn-white-selected me-2 px-4' : 'btn btn-white me-2 px-4'}`} onClick={() => handleDirectorNominalChange(true)}>{t('kyb_flow.org_members_card.modal.yes')}</button>
                      <button type="button" className={`btn ${directorNominal === false ? 'btn btn-white-selected px-4' : 'btn btn-white px-4'}`} onClick={() => handleDirectorNominalChange(false)}>{t('kyb_flow.org_members_card.modal.no')}</button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="inputPep" className="form-label">{t('kyb_flow.org_members_card.modal.pep')}</label>
                    <div className="d-flex">
                      <button type="button" className={`btn ${pep === true ? 'btn btn-white-selected me-2 px-4' : 'btn btn-white me-2 px-4'}`} onClick={() => handlePepChange(true)}>{t('kyb_flow.org_members_card.modal.yes')}</button>
                      <button type="button" className={`btn ${pep === false ? 'btn btn-white-selected px-4' : 'btn btn-white px-4'}`} onClick={() => handlePepChange(false)}>{t('kyb_flow.org_members_card.modal.no')}</button>
                    </div>
                  </div>

                  {pep &&
                    <>
                      <div className="mb-3">
                        <label htmlFor="inputPepEntidad" className="form-label">{t('kyb_flow.org_members_card.modal.pep_entidad')}</label>
                        <input type="text" name='pep_entidad' className="form-control less-rounded" id="inputPepEntidad" aria-describedby="pepEntidadHelp" 
                          value={pepEntidad} onChange={handleInputChange} onBlur={handleBlur}/>
                      </div>
                      <div className="mb-3">
                        <label htmlFor="inputPepCargo" className="form-label">{t('kyb_flow.org_members_card.modal.pep_cargo')}</label>
                        <input type="text" name='pep_cargo' className="form-control less-rounded" id="inputPepCargo" aria-describedby="pepCargoHelp" 
                          value={pepCargo} onChange={handleInputChange} onBlur={handleBlur}/>
                      </div>
                      <div className="mb-3">
                        <label htmlFor="inputPepStartDate" className="form-label">{t('kyb_flow.org_members_card.modal.pep_start_date')}</label>
                        <input type="date" name='pep_fecha_inicio' className="form-control less-rounded" id="inputPepStartDate" aria-describedby="pepStartDateHelp" 
                          value={pepFechaInicio} onChange={handleInputChange} onBlur={handleBlur}/>
                      </div>
                    </>
                  }
                </div>
              </form>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" data-bs-dismiss="modal">{t('cancel')}</button>
            <button type="button" className="btn btn-black" disabled={hasErrors()} onClick={onSubmit}>
              {currentStep === 1 && t('continue')}
              {currentStep === 2 && t('save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CompanyRoleFormModalProps {
  modalId: string;
  index: number;
  member: Contact;
  globalStake: number;
  onMemberUpdate: (index: number, member: Contact) => void;
}

export default CompanyRoleFormModal;